import { salesChannelList } from 'src/components/pages/salesChannels/helpers';
import { stringFormat, T, toLocalID } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';

/** What the agent needs of a store product to retrieve it. */
export interface AgentStoreProduct {
    name: string;
    sku: string;
}

/** A connected store, named the way the Sales channels page names it. Empty platform means none. */
export interface AgentStore {
    salesChannelID?: string;
    salesChannelName: string;
    platform: string;
    storeUrl: string;
}

/**
 * The store behind a sales channel. Without an ID, the first connected one: signup leaves a placeholder channel
 * behind (ecommerceName "NONE"), which is not a connected store.
 */
export const useSalesChannelStore = (salesChannelID?: string): AgentStore => {
    const { data } = useAccountData();

    const salesChannel = salesChannelID
        ? data?.salesChannels?.find((channel) => toLocalID(channel.id) === toLocalID(salesChannelID))
        : data?.salesChannels?.find((channel) => channel.ecommerceSettings?.ecommerceName && channel.ecommerceSettings.ecommerceName !== 'NONE');
    const ecommerceName = salesChannel?.ecommerceSettings?.ecommerceName ?? '';

    return {
        salesChannelID: salesChannel?.id,
        salesChannelName: salesChannel?.detail?.properties?.name ?? '',
        platform: salesChannelList.find((entry) => entry.value === ecommerceName)?.label ?? ecommerceName,
        storeUrl: salesChannel?.ecommerceSettings?.definitiveUrl || salesChannel?.ecommerceSettings?.remoteUrl || ''
    };
};

/**
 * First message for a product that comes from a store: it names the sales channel too, so the agent knows which
 * catalogue the product comes from without asking. Without a sales channel (e.g. a simulated store) only the platform.
 */
export const storeProductPrompt = (product: AgentStoreProduct, store: AgentStore) => {
    if (!store.salesChannelID)
        return stringFormat(T._('Retrieve the product {0} ({1}) from the {2} store', 'Admin'), product.name, product.sku, store.platform);

    return stringFormat(
        T._('Retrieve the product {0} ({1}) from the {2} store, sales channel "{3}" (ID {4})', 'Admin'),
        product.name,
        product.sku,
        store.platform,
        store.salesChannelName || store.storeUrl,
        String(toLocalID(store.salesChannelID) ?? '')
    );
};

/** First message for a product made from scratch, with no store behind it: the agent asks what it is. */
export const scratchProductPrompt = (productName?: string) =>
    productName?.trim()
        ? stringFormat(T._('I want to create a new customizable product from scratch, called "{0}".', 'Admin'), productName.trim())
        : T._('I want to create a new customizable product from scratch.', 'Admin');

/** The product configuration's conversation: kept while the merchant stays in the configuration, discarded on leaving it. */
export const PRODUCT_CONFIGURATION_AGENT_KEY = 'product-configuration';
